'use client'
import React, { FC, HTMLAttributes, useEffect } from 'react'
import { CloseIcon } from '@/root/ui/shared/icons/CloseIcon'

export interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  isOpen: boolean
  onClose: () => void
  hideCloseButton?: boolean
  // When true, the modal cannot be dismissed implicitly (backdrop click or Escape) — only via an
  // explicit in-content action. Use for mandatory modals (e.g. session prolong) where dismissing
  // would leave the user with no way to complete the required action.
  disableDismiss?: boolean
}

export const Modal: FC<ModalProps> = ({
  children,
  isOpen,
  onClose,
  hideCloseButton = false,
  disableDismiss = false,
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      if (!disableDismiss) window.addEventListener('keydown', handleKeyDown)
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose, disableDismiss])

  if (!isOpen) return null

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!disableDismiss && event.target === event.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex min-w-97.5 items-start justify-center bg-black/30"
      onClick={handleOverlayClick}
    >
      <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl sm:relative sm:top-[30%] sm:mx-auto">
        {!hideCloseButton && (
          <div className="flex justify-end pb-2">
            <button onClick={onClose}>
              <CloseIcon color="neutral-medium-light" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  )
}
